import {
  defaultCultureForLanguage,
  languageFromCulture,
  resolveBrowserCulture,
  resolveBrowserTimeZone,
} from '../culture';
import { isSiteLanguage, parseLocalizedPathname } from '../../seo/localeUrls';
import type { CultureCode, LanguageCode, LocalizationContext, TenantId } from '../types';

const TENANT_QUERY_KEY = 'tenant';

export interface ResolvedLocalizationRequest {
  context: LocalizationContext;
}

/**
 * SPA giriş middleware'i — ASP.NET RequestLocalizationMiddleware karşılığı.
 * Sıra: tenant → culture/language → timezone/currency
 */
export function resolveLocalizationRequest(
  overrides?: Partial<LocalizationContext>,
): ResolvedLocalizationRequest {
  const tenantId = resolveTenantId(overrides?.tenantId);
  const culture = resolveCulture(overrides?.culture);
  const language =
    overrides?.language ?? resolveLanguageFromUrl() ?? languageFromCulture(culture);

  const context: LocalizationContext = {
    tenantId,
    language,
    culture,
    timeZone: overrides?.timeZone ?? readStoredTimeZone() ?? resolveBrowserTimeZone(),
    currency: overrides?.currency ?? defaultCurrency(language),
  };

  persistContext(context);
  applyDocumentCulture(context);

  return { context };
}

function resolveTenantId(explicit?: TenantId): TenantId {
  if (explicit) return explicit;

  if (typeof window !== 'undefined') {
    const fromQuery = new URLSearchParams(window.location.search).get(TENANT_QUERY_KEY);
    if (fromQuery) return fromQuery;

    const fromStorage = localStorage.getItem('luminary_tenant');
    if (fromStorage) return fromStorage;
  }

  return import.meta.env.VITE_TENANT_ID ?? 'luminary';
}

function resolveLanguageFromUrl(): LanguageCode | null {
  if (typeof window === 'undefined') return null;
  const { lang } = parseLocalizedPathname(window.location.pathname);
  return lang && isSiteLanguage(lang) ? lang : null;
}

function resolveCulture(explicit?: CultureCode): CultureCode {
  if (explicit) return explicit;

  const fromUrl = resolveLanguageFromUrl();
  if (fromUrl) return defaultCultureForLanguage(fromUrl);

  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem('luminary_culture');
    if (stored) return stored;
  }

  const storedLang = typeof localStorage !== 'undefined' ? localStorage.getItem('i18nextLng') : null;
  if (storedLang === 'tr' || storedLang === 'en') {
    return defaultCultureForLanguage(storedLang);
  }

  return resolveBrowserCulture();
}

function readStoredTimeZone(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem('luminary_timezone');
}

function defaultCurrency(language: LanguageCode): string {
  return language === 'tr' ? 'TRY' : 'USD';
}

function persistContext(context: LocalizationContext): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem('luminary_tenant', context.tenantId);
  localStorage.setItem('luminary_culture', context.culture);
  localStorage.setItem('i18nextLng', context.language);
  localStorage.setItem('luminary_timezone', context.timeZone);
}

/** HTML lang + dir — erişilebilirlik ve tarayıcı yardımcıları */
function applyDocumentCulture(context: LocalizationContext): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = context.language;
  document.documentElement.setAttribute('data-tenant', context.tenantId);
  document.documentElement.setAttribute('data-culture', context.culture);
}
